/** Adapter between the API treasury payloads (raw Prisma) and the UI types. */

import type {
  Currency,
  MovementCategory,
  MovementType,
  TreasuryAccount,
  TreasuryBalanceAdjustment,
  TreasuryMovement,
  TreasuryReconciliation,
} from "@/types";

interface AccountRef {
  id: string;
  name: string;
  currency?: Currency;
}

export interface ApiTreasuryAccount {
  id: string;
  name: string;
  currency: Currency;
  openingBalance: number | string;
  balance: number | string;
  active: boolean;
  note?: string | null;
  // Champs étendus (spec compta §6.1). L'API les renvoie déjà ; sans les
  // transporter, le formulaire d'édition les remettrait à vide en enregistrant.
  accountType?: string | null;
  responsibleId?: string | null;
  bankName?: string | null;
  numberLast?: string | null;
  color?: string | null;
  allowNegative?: boolean | null;
}

export interface ApiMovement {
  id: string;
  type: MovementType;
  category: MovementCategory;
  accountId: string;
  counterAccountId?: string | null;
  amount: number | string;
  currency: Currency;
  exchangeRate?: number | string | null;
  receivedAmount?: number | string | null;
  date: string;
  payee?: string | null;
  reference?: string | null;
  note?: string | null;
  account?: AccountRef | null;
  counterAccount?: AccountRef | null;
  client?: { id: string; name: string } | null;
}

export interface ApiReconciliation {
  id: string;
  accountId: string;
  month: string;
  realBalance: number | string;
  computedBalance: number | string;
  gap: number | string;
  reason?: string | null;
  account?: AccountRef | null;
}

export interface ApiBalanceAdjustment {
  id: string;
  accountId: string;
  currency: Currency;
  previousBalance: number | string;
  newBalance: number | string;
  delta: number | string;
  reason: string;
  createdAt: string;
  account?: AccountRef | null;
}

export interface AdjustBalanceInput {
  accountId: string;
  newBalance: number;
  reason: string;
}

export interface MovementInput {
  type: "IN" | "OUT";
  category: MovementCategory;
  accountId: string;
  amount: number;
  payee?: string;
  reference?: string;
  note?: string;
}

export interface TransferInput {
  fromAccountId: string;
  toAccountId: string;
  amount: number;
  exchangeRate?: number;
  /** spec compta §6.3 — explicit received amount + transfer fees */
  receivedAmount?: number;
  fees?: number;
  reference?: string;
  note?: string;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export function toTreasuryAccount(api: ApiTreasuryAccount): TreasuryAccount {
  return {
    id: api.id,
    name: api.name,
    currency: api.currency,
    openingBalance: toNumber(api.openingBalance),
    balance: toNumber(api.balance),
    active: Boolean(api.active),
    note: api.note ?? undefined,
    accountType: api.accountType ?? undefined,
    responsibleId: api.responsibleId ?? undefined,
    bankName: api.bankName ?? undefined,
    numberLast: api.numberLast ?? undefined,
    color: api.color ?? undefined,
    allowNegative: api.allowNegative ?? undefined,
  };
}

export function toTreasuryMovement(api: ApiMovement): TreasuryMovement {
  return {
    id: api.id,
    type: api.type,
    category: api.category,
    accountId: api.accountId,
    accountName: api.account?.name ?? "—",
    counterAccountId: api.counterAccountId ?? undefined,
    counterAccountName: api.counterAccount?.name ?? undefined,
    amount: toNumber(api.amount),
    currency: api.currency,
    exchangeRate: api.exchangeRate != null ? toNumber(api.exchangeRate) : undefined,
    receivedAmount: api.receivedAmount != null ? toNumber(api.receivedAmount) : undefined,
    date: formatDate(api.date),
    clientName: api.client?.name,
    payee: api.payee ?? undefined,
    reference: api.reference ?? undefined,
    note: api.note ?? undefined,
  };
}

export function toTreasuryBalanceAdjustment(
  api: ApiBalanceAdjustment,
): TreasuryBalanceAdjustment {
  return {
    id: api.id,
    accountId: api.accountId,
    accountName: api.account?.name ?? "—",
    currency: api.currency ?? api.account?.currency ?? "DZD",
    previousBalance: toNumber(api.previousBalance),
    newBalance: toNumber(api.newBalance),
    delta: toNumber(api.delta),
    reason: api.reason,
    createdAt: formatDate(api.createdAt),
  };
}

export function toTreasuryReconciliation(api: ApiReconciliation): TreasuryReconciliation {
  return {
    id: api.id,
    accountId: api.accountId,
    accountName: api.account?.name ?? "—",
    month: api.month?.slice(0, 7) ?? "—",
    realBalance: toNumber(api.realBalance),
    computedBalance: toNumber(api.computedBalance),
    gap: toNumber(api.gap),
    reason: api.reason ?? undefined,
  };
}
